import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPACES = ["Villa", "Residence", "Flat", "Commercial", "Restaurant", "Hotel", "Café / Lounge"];
const BUDGETS = ["₹50K – ₹1L", "₹1L – ₹3L", "₹3L – ₹7L", "₹7L+"];

const DETAILS = [
  { k: "Showroom", v: "Malviya Nagar, Jaipur — Rajasthan" },
  { k: "Booking", v: "Online · pan-India delivery" },
  { k: "Phone", v: "+91 ····· ·······" },
  { k: "Email", v: "hello@jaipurfurniture.com" },
  { k: "Web", v: "www.jaipurfurniture.com" },
];

export default function Contact({ selectedProduct }) {
  const [space, setSpace] = useState("Villa");
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", budget: BUDGETS[1], message: "" });
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState(null);
  const [paying, setPaying] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/orders`, { ...form, space_type: space, product_name: selectedProduct || "" });
      setBooked(r.data);
      toast.success("Booking received — our team will call you within 24 hours to confirm.");
      setForm({ name: "", phone: "", email: "", city: "", budget: BUDGETS[1], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const payAdvance = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        order_id: booked.id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error("Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-oxblood/30 focus:border-brass py-4 text-ink placeholder:text-ink/35 font-light transition-colors duration-300";

  return (
    <section id="book" data-testid="booking-section" className="bg-cream py-28 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 md:gap-24">
        <Reveal>
          <img src="/logo.png" alt="Jaipur Furniture by Yojna Jaisingh" className="h-24 w-auto mb-10" />
          <p className="text-oxblood text-xs tracking-[0.45em] uppercase mb-6">Online Booking</p>
          <h2 className="font-display text-oxblood-ink text-4xl md:text-6xl leading-tight">
            Book your <em className="text-oxblood font-light">furniture.</em>
          </h2>
          <p className="mt-8 text-ink/60 font-light leading-relaxed max-w-md text-base">
            Tell us what you're furnishing — a villa, a restaurant, a hotel or your first flat.
            Place your booking online and our team calls you within 24 hours to confirm
            materials, timelines and delivery.
          </p>
          <div className="mt-12 space-y-6">
            {DETAILS.map((d) => (
              <div key={d.k} data-testid={`booking-detail-${d.k.toLowerCase()}`} className="flex gap-6 items-baseline border-b border-oxblood/15 pb-5">
                <span className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase w-24 shrink-0">{d.k}</span>
                <span className="font-display text-oxblood-ink text-lg md:text-xl">{d.v}</span>
              </div>
            ))}
          </div>
          <p className="mt-10 text-ink/55 font-light text-sm leading-relaxed max-w-md">
            Made to order in our own Jaipur factory. End-to-end service — from material
            selection to delivery and installation at your doorstep.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          {booked ? (
            <div data-testid="booking-success" className="bg-ivory border border-brass/60 p-8 md:p-12 text-center">
              <span className="inline-flex w-14 h-14 rounded-full bg-oxblood items-center justify-center text-brass-soft text-2xl">✓</span>
              <p className="mt-6 font-display text-oxblood-ink text-3xl">Booking received.</p>
              <p className="mt-4 text-ink/60 font-light text-sm leading-relaxed max-w-sm mx-auto">
                Thank you, {booked.name}. Our team will call {booked.phone} within 24 hours to
                confirm materials, pricing and delivery.
              </p>
              <div className="mt-10 border-t border-oxblood/15 pt-8">
                <p className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase mb-4">Secure your piece now</p>
                <p className="text-ink/55 font-light text-sm mb-6 max-w-sm mx-auto">
                  Pay a fully adjustable ₹5,000 advance online — it is adjusted in your final invoice.
                </p>
                <button
                  data-testid="pay-advance-btn"
                  onClick={payAdvance}
                  disabled={paying}
                  className="w-full bg-brass text-oxblood-ink hover:bg-brass-soft py-5 text-xs tracking-[0.3em] uppercase transition-colors duration-400 disabled:opacity-60"
                >
                  {paying ? "Opening secure checkout…" : "Pay ₹5,000 Advance"}
                </button>
                <button
                  data-testid="book-another-btn"
                  onClick={() => setBooked(null)}
                  className="mt-5 text-oxblood/60 hover:text-oxblood text-xs tracking-[0.25em] uppercase transition-colors duration-300"
                >
                  Skip — pay on the confirmation call
                </button>
              </div>
            </div>
          ) : (
          <form data-testid="booking-form" onSubmit={submit} className="bg-ivory border border-oxblood/15 p-8 md:p-12">
            <p className="font-display text-oxblood-ink text-2xl">Booking Details</p>

            {selectedProduct ? (
              <div data-testid="booking-selected-product" className="mt-6 flex items-center justify-between bg-oxblood/5 border border-brass/50 px-5 py-4">
                <span className="font-display italic text-oxblood text-lg">{selectedProduct}</span>
                <span className="text-brass text-[10px] tracking-[0.3em] uppercase">Selected piece</span>
              </div>
            ) : null}

            <div className="mt-8">
              <p className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase mb-4">What are you furnishing?</p>
              <div className="flex flex-wrap gap-2" data-testid="booking-space-tabs">
                {SPACES.map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`space-tab-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setSpace(s)}
                    className={`px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase border transition-colors duration-300 ${
                      space === s ? "bg-oxblood text-ivory border-oxblood" : "border-oxblood/30 text-oxblood hover:border-brass"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 space-y-7">
              <input data-testid="booking-name-input" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
              <input data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone *" className={inputCls} />
              <input data-testid="booking-email-input" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
              <input data-testid="booking-city-input" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
              <select data-testid="booking-budget-select" value={form.budget} onChange={set("budget")} className={`${inputCls} cursor-pointer`}>
                {BUDGETS.map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </select>
              <textarea data-testid="booking-message-input" value={form.message} onChange={set("message")} placeholder="Pieces, quantities, timelines — tell us what you need" rows={3} className={`${inputCls} resize-none`} />
            </div>

            <button
              data-testid="booking-submit-btn"
              type="submit"
              disabled={sending}
              className="mt-12 w-full bg-oxblood text-ivory hover:bg-oxblood-ink py-5 text-xs tracking-[0.3em] uppercase transition-colors duration-400 disabled:opacity-60"
            >
              {sending ? "Placing booking…" : "Place Booking"}
            </button>
            <p className="mt-4 text-center text-ink/45 text-xs font-light">
              No advance payment — confirmation call first, payment later.
            </p>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
